import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { baseURL } from '../../config'
import {
	registerPasskey,
	listPasskeys,
	deletePasskey,
	renamePasskey,
} from '../../services/passkeyService'
import ReauthModal from './ReauthModal'

export default function SecuritySettings({ isDarkMode }) {
	const [passkeys, setPasskeys] = useState([])
	const [loading, setLoading] = useState(true)
	const [registerLoading, setRegisterLoading] = useState(false)
	const [newPasskeyName, setNewPasskeyName] = useState('')
	const [showAddForm, setShowAddForm] = useState(false)
	const [error, setError] = useState('')
	const [twoFactorEnabled, setTwoFactorEnabled] = useState(true)
	const [toggling2FA, setToggling2FA] = useState(false)
	const [editingId, setEditingId] = useState(null)
	const [editName, setEditName] = useState('')
	const [pendingAction, setPendingAction] = useState(null)
	const [hasPassword, setHasPassword] = useState(true)
	const [showPasswordForm, setShowPasswordForm] = useState(false)
	const [currentPassword, setCurrentPassword] = useState('')
	const [newPassword, setNewPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [passwordLoading, setPasswordLoading] = useState(false)
	const [passwordSuccess, setPasswordSuccess] = useState('')

	useEffect(() => {
		loadPasskeys()
		loadTwoFactorStatus()
	}, [])

	const loadPasskeys = async () => {
		try {
			setLoading(true)
			const data = await listPasskeys()
			setPasskeys(data)
		} catch {
			setError('Failed to load passkeys')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (!error) return
		const timer = setTimeout(() => setError(''), 20000)
		return () => clearTimeout(timer)
	}, [error])

	useEffect(() => {
		if (!passwordSuccess) return
		const timer = setTimeout(() => setPasswordSuccess(''), 20000)
		return () => clearTimeout(timer)
	}, [passwordSuccess])

	const loadTwoFactorStatus = async () => {
		try {
			const res = await axios.get(`${baseURL}/api/user/security-settings`)
			setTwoFactorEnabled(res.data.twoFactorEnabled)
			setHasPassword(res.data.hasPassword)
		} catch {
			// Default to enabled
		}
	}

	const requireReauth = useCallback((action) => {
		setPendingAction(() => action)
	}, [])

	const handleReauthVerified = useCallback(async (credentials) => {
		const action = pendingAction
		setPendingAction(null)
		if (action) {
			try {
				await action(credentials)
			} catch (err) {
				const errorMsg = err.response?.data?.error
				if (errorMsg === 'Incorrect password') {
					setError('Incorrect password. Please try again.')
				} else if (err.response?.data?.requiresEmailReauth) {
					setError('This account uses Google sign-in. Please use the email code option.')
				} else {
					setError(errorMsg || 'Operation failed')
				}
			}
		}
	}, [pendingAction])

	const handleReauthCancel = useCallback(() => {
		setPendingAction(null)
		setToggling2FA(false)
		setRegisterLoading(false)
	}, [])

	const handleRegister = () => {
		setError('')
		requireReauth(async (credentials) => {
			setRegisterLoading(true)
			try {
				await registerPasskey(newPasskeyName || 'My Passkey', credentials)
				setShowAddForm(false)
				setNewPasskeyName('')
				await loadPasskeys()
			} catch (err) {
				if (err.name === 'NotAllowedError') {
					setError('Passkey registration was cancelled')
				} else {
					throw err
				}
			} finally {
				setRegisterLoading(false)
			}
		})
	}

	const handleDelete = credentialId => {
		setError('')
		requireReauth(async (credentials) => {
			try {
				await deletePasskey(credentialId, credentials)
				await loadPasskeys()
				await loadTwoFactorStatus()
			} catch (err) {
				setError(err.response?.data?.error || 'Failed to delete passkey')
			}
		})
	}

	const handleRename = async credentialId => {
		if (!editName.trim()) return
		try {
			await renamePasskey(credentialId, editName)
			setEditingId(null)
			setEditName('')
			await loadPasskeys()
		} catch {
			setError('Failed to rename passkey')
		}
	}

	const handleToggle2FA = () => {
		setError('')

		if (twoFactorEnabled) {
			requireReauth(async (credentials) => {
				setToggling2FA(true)
				try {
					const res = await axios.put(`${baseURL}/api/user/security-settings`, {
						twoFactorEnabled: false,
						...credentials,
					})
					setTwoFactorEnabled(res.data.twoFactorEnabled)
				} finally {
					setToggling2FA(false)
				}
			})
		} else {
			toggleWithoutReauth()
		}
	}

	const toggleWithoutReauth = async () => {
		setToggling2FA(true)
		try {
			const res = await axios.put(`${baseURL}/api/user/security-settings`, {
				twoFactorEnabled: true,
			})
			setTwoFactorEnabled(res.data.twoFactorEnabled)
		} catch {
			setError('Failed to update 2FA setting')
		} finally {
			setToggling2FA(false)
		}
	}

	const handleChangePassword = async () => {
		setError('')
		setPasswordSuccess('')

		if (!newPassword.trim() || !confirmPassword.trim()) {
			return setError('Please fill in all password fields')
		}
		if (newPassword.trim().length < 8) {
			return setError('New password must be at least 8 characters')
		}
		if (newPassword.trim() !== confirmPassword.trim()) {
			return setError('Passwords do not match')
		}

		setPasswordLoading(true)
		try {
			await axios.put(`${baseURL}/api/user/change-password`, {
				currentPassword: currentPassword || undefined,
				newPassword,
				confirmPassword,
			})
			setPasswordSuccess('Password updated successfully')
			setShowPasswordForm(false)
			setCurrentPassword('')
			setNewPassword('')
			setConfirmPassword('')
			setHasPassword(true)
		} catch (err) {
			setError(err.response?.data?.error || 'Failed to change password')
		} finally {
			setPasswordLoading(false)
		}
	}

	const resetPasswordForm = () => {
		setShowPasswordForm(false)
		setCurrentPassword('')
		setNewPassword('')
		setConfirmPassword('')
		setPasswordSuccess('')
	}

	const textColor = isDarkMode ? 'text-gray-200' : 'text-gray-900'
	const subTextColor = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const borderColor = isDarkMode ? 'border-gray-600' : 'border-gray-200'
	const cardBg = isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'

	return (
		<div>
			<h2 className={`text-xl font-semibold ${textColor} mb-6`}>Security</h2>

			{error && (
				<div className={`rounded-lg p-3 mb-4 text-sm border ${
					isDarkMode
						? 'bg-red-900/30 text-red-300 border-red-800'
						: 'bg-red-50 text-red-700 border-red-200'
				}`}>
					{error}
					<button
						onClick={() => setError('')}
						className={`float-right ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
					>
						&times;
					</button>
				</div>
			)}

			{/* Two-Factor Authentication Section */}
			<div className={`border ${borderColor} rounded-lg p-5 mb-6`}>
				<div className="flex items-center justify-between mb-2">
					<h3 className={`text-base font-medium ${textColor}`}>
						Email Two-Factor Authentication
					</h3>
					<button
						onClick={handleToggle2FA}
						disabled={toggling2FA}
						className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
							twoFactorEnabled ? 'bg-blue-600' : 'bg-gray-300'
						}`}
					>
						<span
							className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
								twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
							}`}
						/>
					</button>
				</div>
				<p className={`text-sm ${subTextColor}`}>
					When enabled, you'll receive a verification code via email when logging in
					from a new device or browser.
				</p>
			</div>

			{/* Password Section */}
			<div className={`border ${borderColor} rounded-lg p-5 mb-6`}>
				<div className="flex items-center justify-between mb-2">
					<div>
						<h3 className={`text-base font-medium ${textColor}`}>Password</h3>
						<p className={`text-sm ${subTextColor} mt-1`}>
							{hasPassword
								? 'Update your password to keep your account secure.'
								: 'Set a password to enable email/password login.'}
						</p>
					</div>
					{!showPasswordForm && (
						<button
							onClick={() => {
								setError('')
								setPasswordSuccess('')
								setShowPasswordForm(true)
							}}
							className="text-sm font-medium text-blue-600 hover:text-blue-700"
						>
							{hasPassword ? 'Change password' : 'Set password'}
						</button>
					)}
				</div>

				{passwordSuccess && (
					<div className={`rounded-lg p-3 mt-3 text-sm border ${
						isDarkMode
							? 'bg-green-900/30 text-green-300 border-green-800'
							: 'bg-green-50 text-green-700 border-green-200'
					}`}>
						{passwordSuccess}
					</div>
				)}

				{showPasswordForm && (
					<div className={`${cardBg} rounded-lg p-4 mt-3 space-y-3`}>
						{hasPassword && (
							<div>
								<label className={`block text-sm font-medium ${textColor} mb-1`}>
									Current password
								</label>
								<input
									type="password"
									value={currentPassword}
									onChange={e => setCurrentPassword(e.target.value)}
									className={`w-full rounded-md border ${borderColor} px-3 py-2 text-sm ${
										isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
									}`}
								/>
							</div>
						)}
						<div>
							<label className={`block text-sm font-medium ${textColor} mb-1`}>
								New password
							</label>
							<input
								type="password"
								value={newPassword}
								onChange={e => setNewPassword(e.target.value)}
								className={`w-full rounded-md border ${borderColor} px-3 py-2 text-sm ${
									isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
								}`}
							/>
						</div>
						<div>
							<label className={`block text-sm font-medium ${textColor} mb-1`}>
								Confirm new password
							</label>
							<input
								type="password"
								value={confirmPassword}
								onChange={e => setConfirmPassword(e.target.value)}
								onKeyDown={e => {
									if (e.key === 'Enter') handleChangePassword()
								}}
								className={`w-full rounded-md border ${borderColor} px-3 py-2 text-sm ${
									isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
								}`}
							/>
						</div>
						<div className="flex gap-2 pt-1">
							<button
								onClick={handleChangePassword}
								disabled={passwordLoading}
								className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
							>
								{passwordLoading ? 'Saving...' : 'Save password'}
							</button>
							<button
								onClick={resetPasswordForm}
								className={`px-3 py-2 text-sm ${subTextColor}`}
							>
								Cancel
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Passkeys Section */}
			<div className={`border ${borderColor} rounded-lg p-5`}>
				<div className="flex items-center justify-between mb-4">
					<div>
						<h3 className={`text-base font-medium ${textColor}`}>Passkeys</h3>
						<p className={`text-sm ${subTextColor} mt-1`}>
							Use fingerprint, face recognition, or security key to sign in without a password.
						</p>
					</div>
					{!showAddForm && (
						<button
							onClick={() => setShowAddForm(true)}
							className="text-sm font-medium text-blue-600 hover:text-blue-700"
						>
							+ Add passkey
						</button>
					)}
				</div>

				{showAddForm && (
					<div className={`${cardBg} rounded-lg p-4 mb-4`}>
						<label className={`block text-sm font-medium ${textColor} mb-1`}>
							Passkey name
						</label>
						<div className="flex gap-2">
							<input
								type="text"
								value={newPasskeyName}
								onChange={e => setNewPasskeyName(e.target.value)}
								placeholder="e.g. MacBook Pro"
								maxLength={50}
								className={`flex-1 rounded-md border ${borderColor} px-3 py-2 text-sm ${
									isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
								}`}
							/>
							<button
								onClick={handleRegister}
								disabled={registerLoading}
								className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
							>
								{registerLoading ? 'Registering...' : 'Register'}
							</button>
							<button
								onClick={() => {
									setShowAddForm(false)
									setNewPasskeyName('')
								}}
								className={`px-3 py-2 text-sm ${subTextColor} hover:${textColor}`}
							>
								Cancel
							</button>
						</div>
					</div>
				)}

				{loading ? (
					<div className={`text-sm ${subTextColor} py-4 text-center`}>
						Loading passkeys...
					</div>
				) : passkeys.length === 0 ? (
					<div className={`text-sm ${subTextColor} py-4 text-center`}>
						No passkeys registered yet.
					</div>
				) : (
					<div className="space-y-3">
						{passkeys.map(pk => (
							<div
								key={pk.credentialId}
								className={`flex items-center justify-between ${cardBg} rounded-lg p-3`}
							>
								<div className="flex items-center gap-3">
									<svg
										className={`w-5 h-5 ${subTextColor}`}
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										strokeWidth={2}
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"
										/>
									</svg>
									<div>
										{editingId === pk.credentialId ? (
											<div className="flex items-center gap-2">
												<input
													type="text"
													value={editName}
													onChange={e => setEditName(e.target.value)}
													onKeyDown={e => {
														if (e.key === 'Enter') handleRename(pk.credentialId)
														if (e.key === 'Escape') {
															setEditingId(null)
															setEditName('')
														}
													}}
													maxLength={50}
													className={`rounded border ${borderColor} px-2 py-1 text-sm ${
														isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'
													}`}
													autoFocus
												/>
												<button
													onClick={() => handleRename(pk.credentialId)}
													className="p-1 text-green-500 hover:text-green-400"
												>
													<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
													</svg>
												</button>
												<button
													onClick={() => {
														setEditingId(null)
														setEditName('')
													}}
													className="p-1 text-red-500 hover:text-red-400"
												>
													<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
													</svg>
												</button>
											</div>
										) : (
											<div className="flex items-center gap-2">
												<p className={`text-sm font-medium ${textColor}`}>
													{pk.friendlyName}
												</p>
												<button
													onClick={() => {
														setEditingId(pk.credentialId)
														setEditName(pk.friendlyName)
													}}
													className={`p-1 rounded hover:bg-gray-700/50 ${
														isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
													}`}
													title="Rename passkey"
												>
													<svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
													</svg>
												</button>
											</div>
										)}
										{editingId !== pk.credentialId && (
											<p className={`text-xs ${subTextColor}`}>
												{pk.lastUsedAt
													? `Last used ${new Date(pk.lastUsedAt).toLocaleDateString()}`
													: `Added ${new Date(pk.createdAt).toLocaleDateString()}`}
												{pk.credentialBackedUp && ' · Synced'}
											</p>
										)}
									</div>
								</div>

								{editingId !== pk.credentialId && (
									<button
										onClick={() => handleDelete(pk.credentialId)}
										className="text-xs text-red-500 hover:text-red-700"
									>
										Remove
									</button>
								)}
							</div>
						))}
					</div>
				)}
			</div>

			{pendingAction && (
				<ReauthModal
					isDarkMode={isDarkMode}
					hasPassword={hasPassword}
					onVerified={handleReauthVerified}
					onCancel={handleReauthCancel}
				/>
			)}
		</div>
	)
}
